import type { FlightSegmentSource } from '../../flight/results/flight_offer_source.js';

/*
 * The checkout organism's own vocabulary (#5000). The kit never imports the
 * genui wire, so these structural types mirror it: the host's payloads fit them
 * without conversion, and the genui-kit pins each one against the wire in a
 * type test. Shapes the kit hands BACK to the host (drafts, edits, prefill, the
 * booking form slices the submit builder reads) mirror the wire exactly; the
 * item and request shapes declare only what the kit reads.
 */

export type CheckoutProductType = 'rental' | 'hotel' | 'flight' | 'bus' | 'mobility';

export interface CheckoutMoney {
  amount: number;
  currency: string;
}

/** A picked option echoed back on submit (`{ id, name }`). */
export interface CheckoutNamedRef {
  id: string;
  name: string;
}

/** A corporate option as the booking form lists it. */
export interface CheckoutOption {
  id: string;
  name: string;
  code?: string | undefined;
}

/** One traveler of the booking roster. */
export interface CheckoutTravelerRef {
  id: string;
  name: string;
}

export type CheckoutDocumentType = 'cpf' | 'passport' | 'dni';

/** The traveler fields the server can flag as missing on the profile. */
export type CheckoutMissingField = 'birthdate' | 'nationality' | 'gender' | 'document' | 'embark';

/** The corporate booking form one traveler's checkout reads: the allocation
 *  catalog, the motive and justification lists and the traveler's profile. */
export interface CheckoutBookingForm {
  companies: {
    id: string;
    name: string;
    allCostCenters?: boolean | undefined;
    costCenters: CheckoutOption[];
    projects: CheckoutOption[];
  }[];
  defaultCompanyId?: string | undefined;
  defaultCostCenterId?: string | undefined;
  motives: CheckoutOption[];
  justifications: CheckoutOption[];
  enableProjectField?: boolean | undefined;
  /** The booker's `allow_many_allocations` flag: the rateio in up to 10 rows. */
  allowManyAllocations?: boolean | undefined;
  traveler?:
    | {
        id?: string | undefined;
        name?: string | undefined;
        firstName?: string | undefined;
        lastName?: string | undefined;
        birthdate?: string | undefined;
        email?: string | undefined;
        phone?: string | undefined;
        nationality?: { code?: string | undefined; name?: string | undefined } | undefined;
        gender?: 'F' | 'M' | undefined;
        document?:
          | {
              type: CheckoutDocumentType;
              value: string;
              issuingCountry?: string | undefined;
              expirationDate?: string | undefined;
            }
          | undefined;
        maskedInfo?:
          | {
              cpf?: string | undefined;
              passport?: string | undefined;
              passportCountry?: string | undefined;
              passportValidate?: string | undefined;
              dni?: string | undefined;
            }
          | undefined;
        missingRequired?: CheckoutMissingField[] | undefined;
      }
    | undefined;
  countries?: { code: string; name: string }[] | undefined;
}

export interface CheckoutDocument {
  type: CheckoutDocumentType;
  value: string;
  issuingCountry?: string | undefined;
  expirationDate?: string | undefined;
}

/** The identity edits one traveler's block carries on submit. */
export interface CheckoutTravelerEdits {
  birthdate?: string | undefined;
  nationality?: { code?: string | undefined; name?: string | undefined } | undefined;
  document?: CheckoutDocument | undefined;
  gender?: 'F' | 'M' | undefined;
  boardingName?: string | undefined;
  boardingSurname?: string | undefined;
  phone?: string | undefined;
  countryCode?: string | undefined;
}

/** The traveler card's draft: the edits, with the CPF selectable in the
 *  document toggle (the submit builder drops a CPF on file). */
export type CheckoutTravelerDraft = Omit<CheckoutTravelerEdits, 'document'> & {
  document?: CheckoutTravelerEdits['document'] | { type: 'cpf'; value: string };
};

/** One field of the product's manifest. `group` picks the form section. */
export type CheckoutField =
  | { kind: 'allocation'; group: 'corporate' }
  | { kind: 'select'; group: 'corporate'; id: 'motive' | 'justification'; labelKey: string }
  | { kind: 'observation'; group: 'corporate'; maxLength: number }
  | { kind: 'birthdate'; group: 'traveler' }
  | { kind: 'nationality'; group: 'traveler' }
  | { kind: 'document'; group: 'traveler'; allow: CheckoutDocumentType[] }
  | { kind: 'gender'; group: 'traveler' }
  | { kind: 'embark'; group: 'traveler' }
  | { kind: 'phone'; group: 'traveler' };

/** What still blocks the submit, named next to the CTA. */
export type CheckoutSubmitBlocker =
  | CheckoutMissingField
  | 'phone'
  | 'allocation'
  | 'project'
  | 'motive'
  | 'justification';

/** The booking fields (motive, justification, observation) handed back on close
 *  so the same quote reopens with them. */
export interface CheckoutBookingDraft {
  motiveId?: string;
  justificationId?: string;
  observation?: string;
}

export interface CheckoutPrefillAllocation {
  companyId: string;
  costCenterId: string;
  projectId?: string | undefined;
}

export interface CheckoutPrefillTraveler {
  id: string;
  allocation?: CheckoutPrefillAllocation | undefined;
  edits?: CheckoutTravelerEdits | undefined;
}

/** What the previous purchase of the same trip (or the one-click card) seeds:
 *  ids and edits only, revalidated against the fresh forms. */
export interface CheckoutPrefill {
  motiveId?: string | undefined;
  justificationId?: string | undefined;
  observation?: string | undefined;
  travelers?: CheckoutPrefillTraveler[] | undefined;
  sourceProductType?: string | undefined;
  capturedAt?: string | undefined;
}

/** The cost center's balance for the budget lighthouse. Advisory only. */
export interface CheckoutBudget {
  hasControl: boolean;
  hasLimit: boolean;
  hasError: boolean;
  balance: number;
  currency?: string;
  symbol?: string;
}

export interface CheckoutBudgetQuery {
  value: number;
  companyId: string;
  costCenterId: string;
  startDate: string;
  offerId?: string;
}

/**
 * A service the same trip already bought, as the "Your trip" panel lists it: a
 * read-only row with its status and the price already paid. The host builds it
 * ready to show; the kit only draws it.
 */
export interface CheckoutTripService {
  /** Stable row identity (the service key in the trip). */
  id: string;
  /** "Latam 8180 · SFO → LAX", "The Inn at Venice Beach". */
  title: string;
  /** Translated voucher status ("Emitido"). Absent ⇒ no pill. */
  statusLabel?: string;
  /** What this service already cost — informative, NEVER added to the total. */
  price?: CheckoutMoney;
}

// ── Items (what the kit reads of each product's offer) ─────────────────────

/** One rule of the policy that rejected the quote (flight only today). */
export interface CheckoutPolicyViolation {
  readonly key: string;
  readonly params?:
    | { readonly days: number; readonly scope: 'domestic' | 'international' }
    | undefined;
}

interface CheckoutItemBase<P extends CheckoutProductType, D> {
  readonly productType: P;
  readonly offerId: string;
  readonly price: CheckoutMoney;
  readonly detail: D & { readonly withinPolicy: boolean };
}

export type RentalItemSource = CheckoutItemBase<
  'rental',
  {
    readonly vehicleName?: string | undefined;
    readonly category: string;
    readonly rentalCompany: string;
    readonly thumbUrl?: string | undefined;
    readonly pickupAt?: string | undefined;
    readonly dropoffAt?: string | undefined;
    readonly pickUpAgency?: string | undefined;
    readonly pickUpAddress?: string | undefined;
    readonly days?: number | undefined;
  }
>;

export type HotelItemSource = CheckoutItemBase<
  'hotel',
  {
    readonly hotelName?: string | undefined;
    readonly roomName?: string | undefined;
    /** The supplier's raw rate name, shown under the canonical label. */
    readonly roomSource?: string | undefined;
    readonly board?: string | undefined;
    readonly address?: string | undefined;
    readonly thumbUrl?: string | undefined;
    readonly checkIn?: string | undefined;
    readonly checkOut?: string | undefined;
    readonly nights?: number | undefined;
    /** Guests per room from the search: who holds each room (#5522). */
    readonly guestsPerRoom?: number | undefined;
    readonly refundable?: boolean | undefined;
    /** Free cancellation until this day (ISO), as the quote confirmed it. */
    readonly freeCancellationUntil?: string | undefined;
  }
>;

export type FlightItemSource = CheckoutItemBase<
  'flight',
  {
    readonly outbound: FlightSegmentSource;
    readonly return?: FlightSegmentSource | undefined;
    readonly fareClass?: string | undefined;
    /** The return fare sold apart; absent ⇒ `fareClass`. */
    readonly returnFareClass?: string | undefined;
    readonly policyViolations?: readonly CheckoutPolicyViolation[] | undefined;
  }
>;

/** A bus stop at one end of the trip. */
export interface CheckoutBusStop {
  readonly stopName: string;
  readonly dateTime: string;
}

export type BusItemSource = CheckoutItemBase<
  'bus',
  {
    readonly departure: CheckoutBusStop;
    readonly arrival: CheckoutBusStop;
    readonly durationMinutes?: number | undefined;
    readonly carrierName?: string | undefined;
    readonly seatClass?: string | undefined;
    readonly connections?: number | undefined;
    /** The leg in a round-trip cart; absent ⇒ a single item. */
    readonly leg?: 'outbound' | 'return' | undefined;
    /** The chosen seats per segment; `[]` = no seat (the VOLL team picks). */
    readonly seats?: readonly (readonly string[])[] | undefined;
  }
>;

/** One leg of a ride: ordered stops; the car with driver has no destination. */
export interface CheckoutMobilityLeg {
  readonly from: string;
  readonly to?: string | undefined;
  readonly scheduleDate: string;
  readonly stops?: readonly string[] | undefined;
}

export type MobilityItemSource = CheckoutItemBase<
  'mobility',
  {
    readonly service: 'cooperative' | 'car_driver';
    readonly supplierName: string;
    readonly outward: CheckoutMobilityLeg;
    readonly return?: CheckoutMobilityLeg | undefined;
    readonly days?: number | undefined;
    readonly hours?: number | undefined;
    readonly km?: number | undefined;
    readonly freeKm?: boolean | undefined;
    readonly vehicle?: string | undefined;
    readonly bilingual?: boolean | undefined;
    readonly overnightDays?: number | undefined;
    /** The flight the ride follows, in the text the cooperative reads. */
    readonly flightNote?: string | undefined;
    /** The estimated price is the direct route's. */
    readonly excludesStops?: boolean | undefined;
  }
>;

/** One item of the cart, discriminated by `productType`. */
export type CheckoutItemSource =
  | RentalItemSource
  | HotelItemSource
  | FlightItemSource
  | BusItemSource
  | MobilityItemSource;

// ── Concierge (the request to the VOLL team) ───────────────────────────────

export type ConciergeProductType = 'flight' | 'hotel' | 'bus' | 'rental';

/** Rental has no "why ask the team" catalog. */
export type ConciergeReasonProductType = Exclude<ConciergeProductType, 'rental'>;

export type ConciergeFlightPeriod = 'morning' | 'afternoon' | 'night' | 'dawn';

interface ConciergeRequestBase<P extends ConciergeProductType> {
  readonly productType: P;
  /** The search profile the request inherits ("Direto", "Manhã"). */
  readonly profile: readonly string[];
}

interface ConciergeAirportRef {
  readonly iata: string;
  readonly city?: string | undefined;
}

interface ConciergeFlightRequestSource extends ConciergeRequestBase<'flight'> {
  readonly segments: readonly {
    readonly date: string;
    readonly origin: ConciergeAirportRef;
    readonly destination: ConciergeAirportRef;
    readonly period?: ConciergeFlightPeriod | undefined;
  }[];
  /** The outbound already chosen online when the request is born on the return. */
  readonly heldOutbound?:
    | {
        readonly segment: FlightSegmentSource;
        readonly price?: CheckoutMoney | undefined;
        readonly fareClass?: string | undefined;
      }
    | undefined;
}

interface ConciergeHotelRequestSource extends ConciergeRequestBase<'hotel'> {
  readonly place: string;
  readonly city?: string | undefined;
  readonly checkIn: string;
  readonly checkOut: string;
}

interface ConciergeBusRequestSource extends ConciergeRequestBase<'bus'> {
  readonly from: { readonly code: string; readonly name: string };
  readonly to: { readonly code: string; readonly name: string };
  readonly outwardDate: string;
  readonly returnDate?: string | undefined;
}

interface ConciergeRentalRequestSource extends ConciergeRequestBase<'rental'> {
  readonly pickup: { readonly ref: string; readonly label: string };
  readonly pickupAt: string;
  readonly dropoffAt: string;
  readonly categoryHint?: string | undefined;
}

/** The request to the VOLL team, as the kit reads it. */
export type ConciergeRequestSource =
  | ConciergeFlightRequestSource
  | ConciergeHotelRequestSource
  | ConciergeBusRequestSource
  | ConciergeRentalRequestSource;

export type ConciergeBedType = 'UNDEFINED' | 'SINGLE' | 'DOUBLE';

export type ConciergeBusSeatPosition = 'FRONT' | 'MIDDLE' | 'BACK';

export type ConciergeBusSeatRowPosition = 'WINDOW_SEAT' | 'AISLE_SEAT';

export type ConciergeBusSeatClass =
  | 'ALL'
  | 'CONVENTIONAL'
  | 'EXECUTIVE'
  | 'HALF_BED'
  | 'BED'
  | 'BED_FULL';

export interface ConciergeReasonOption {
  id: string;
  name: string;
  isDefault?: boolean | undefined;
}

export interface ConciergeRentalOption {
  id: string;
  name: string;
  imageUrl?: string | undefined;
}

export interface ConciergeRentalOptions {
  categories: ConciergeRentalOption[];
  companies: ConciergeRentalOption[];
}

// The concierge vocabularies as lists, in the order the request card offers
// them (the wire's enum order, pinned by the genui-kit type test).
export const CONCIERGE_FLIGHT_PERIODS = [
  'morning',
  'afternoon',
  'night',
  'dawn',
] as const satisfies readonly ConciergeFlightPeriod[];
export const CONCIERGE_BED_TYPES = [
  'UNDEFINED',
  'SINGLE',
  'DOUBLE',
] as const satisfies readonly ConciergeBedType[];
export const CONCIERGE_BUS_SEAT_POSITIONS = [
  'FRONT',
  'MIDDLE',
  'BACK',
] as const satisfies readonly ConciergeBusSeatPosition[];
export const CONCIERGE_BUS_ROW_POSITIONS = [
  'WINDOW_SEAT',
  'AISLE_SEAT',
] as const satisfies readonly ConciergeBusSeatRowPosition[];
export const CONCIERGE_BUS_SEAT_CLASSES = [
  'ALL',
  'CONVENTIONAL',
  'EXECUTIVE',
  'HALF_BED',
  'BED',
  'BED_FULL',
] as const satisfies readonly ConciergeBusSeatClass[];

/** The concierge request's details cap (the wire's `CONCIERGE_DETAILS_MAX`). */
export const CONCIERGE_DETAILS_MAX = 300;
